import styled from "@emotion/styled";
import { text, surface, border as borderToken, interactive, controlFontSize, fontSize, fontWeight, spacing, opacity, duration, borderRadius } from "@atlas-int/tokens";
import type { SelectBoxItem } from "../dropdownPrimitive/types";
import InfoTooltipTrigger from "./InfoTooltipTrigger";

const GroupContainer = styled.div`
  margin: 0;
  padding: ${spacing[4]} ${spacing[6]};
  border-top: 1px solid ${borderToken.subtle};

  &:first-of-type {
    border-top: none;
  }
`;

const GroupLabel = styled.legend`
  font-size: ${controlFontSize.default};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  padding: 0;
  margin-bottom: ${spacing[2]};
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
`;

const RadioOption = styled.label<{ $disabled?: boolean; $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[1]} 0;
  font-size: ${fontSize.lg};
  color: ${({ $disabled, $selected }) => ($disabled && !$selected ? text.disabled : text.primary)};
  cursor: ${({ $disabled, $selected }) => ($disabled && !$selected ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled, $selected }) => ($disabled && !$selected ? opacity.muted : "1")};
  transition: color ${duration.fast};

  &:hover {
    color: ${({ $disabled, $selected }) => ($disabled && !$selected ? undefined : interactive.default)};
    background-color: ${({ $disabled, $selected }) => ($disabled && !$selected ? undefined : surface.hover)};
    border-radius: ${borderRadius.base};
  }

  input[type="radio"] {
    accent-color: ${interactive.default};
    margin: 0;
    cursor: inherit;
  }
`;

interface SettingsRadioGroupProps {
  label: string;
  tooltip?: string;
  items: SelectBoxItem[];
  selectedValue: string | number | undefined;
  onChange: (value: string | number) => void;
}

const SettingsRadioGroup = ({
  label,
  tooltip,
  items,
  selectedValue,
  onChange,
}: SettingsRadioGroupProps) => {
  return (
    <GroupContainer>
      <GroupLabel>
        {label}
        {tooltip && <InfoTooltipTrigger text={tooltip} />}
      </GroupLabel>
      {items.map((item) => {
        const isSelected = selectedValue === item.value;
        return (
          <RadioOption key={String(item.value)} $disabled={item.disabled} $selected={isSelected}>
            <input
              type="radio"
              name={label}
              checked={isSelected}
              disabled={item.disabled && !isSelected}
              onChange={() => onChange(item.value)}
            />
            {typeof item.label === "string" ? item.label : String(item.value)}
          </RadioOption>
        );
      })}
    </GroupContainer>
  );
};

export default SettingsRadioGroup;
